/** @graphql agg **/
import { useMemo } from 'react';
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql, useFragment } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	useFetchSuggestionCountQuery$data,
	useFetchSuggestionCountQuery$variables,
} from './__generated__/useFetchSuggestionCountQuery.graphql.ts';
import type {
	useFetchSuggestionCount_query$data,
	useFetchSuggestionCount_query$key,
} from './__generated__/useFetchSuggestionCount_query.graphql.ts';

export type RemoteObjectSuggestionCountFragmentRef = useFetchSuggestionCount_query$key;

export const RemoteObjectSuggestionCountFragment: GraphQLTaggedNode = graphql`
	fragment useFetchSuggestionCount_query on Query {
		confluence_remoteObjectSuggestionCount(cloudId: $cloudId, spaceKey: $spaceKey)
	}
`;

export const RemoteObjectSuggestionCountQuery: GraphQLTaggedNode = graphql`
	query useFetchSuggestionCountQuery($cloudId: ID!, $spaceKey: String!) {
		...useFetchSuggestionCount_query @relay(mask: false)
	}
`;

export type UseFetchSuggestionCountResult = {
	count: number;
	isLoading: boolean;
	isSuppressed: boolean;
	refetch: () => void;
};

type UseFetchSuggestionCountProps = {
	spaceKey?: string;
	cloudId: string;
};

export const useFetchSuggestionCount = ({
	spaceKey,
	cloudId,
}: UseFetchSuggestionCountProps): UseFetchSuggestionCountResult =>
	useFetchSuggestionCountInternal({ spaceKey, cloudId });

export const useFetchSuggestionCountFromFragment = ({
	spaceKey,
	cloudId,
	suggestionCountFragmentRef,
}: UseFetchSuggestionCountProps & {
	suggestionCountFragmentRef: RemoteObjectSuggestionCountFragmentRef;
}): UseFetchSuggestionCountResult => {
	const fragmentData = useFragment(RemoteObjectSuggestionCountFragment, suggestionCountFragmentRef);

	return useFetchSuggestionCountInternal({ spaceKey, cloudId }, fragmentData);
};

const useFetchSuggestionCountInternal = (
	{ spaceKey, cloudId }: UseFetchSuggestionCountProps,
	fragmentData?: useFetchSuggestionCount_query$data,
): UseFetchSuggestionCountResult => {
	const usesRouteData = fragmentData !== undefined;

	const {
		data: apolloData,
		loading: apolloLoading,
		refetch: apolloRefetch,
	} = useQueryInterop<useFetchSuggestionCountQuery$data, useFetchSuggestionCountQuery$variables>(
		RemoteObjectSuggestionCountQuery,
		{
			variables: { cloudId, spaceKey: spaceKey ?? '' },
			skip: !spaceKey || usesRouteData,
			interopApolloAggClient: true,
		},
	);

	return useMemo<UseFetchSuggestionCountResult>(() => {
		const hasChildQueryData = apolloData != null;
		const raw = hasChildQueryData
			? apolloData.confluence_remoteObjectSuggestionCount
			: fragmentData?.confluence_remoteObjectSuggestionCount;
		const isLoading = usesRouteData && !hasChildQueryData ? false : apolloLoading;
		// null data is the suppression signal — this includes the case where the
		// backend returns errors alongside null data (e.g. NotFoundException when
		// the user has been permanently suppressed). Do not gate on apolloError.
		const isSuppressed = !isLoading && raw == null;
		return {
			count: raw ?? 0,
			isLoading,
			isSuppressed,
			refetch: () => {
				void apolloRefetch();
			},
		};
	}, [apolloData, apolloLoading, apolloRefetch, fragmentData, usesRouteData]);
};
